import { Injectable } from '@angular/core';
import { PlanType } from '@app/core/interfaces/c5/enumerations';
import { PhoneNumberType } from '@app/core/interfaces/c5/phone-number-type';
import { Meeting } from '@app/core/interfaces/portal/meeting';
import { MeetingUpdate } from '@app/core/interfaces/portal/meeting-update';
import { Site } from '@app/core/interfaces/site';
import { MeetingRepository, ResetMeetingPinOrTokenType } from '@app/core/repositories/meeting.repository';
import { ContactNamePipe } from '@app/shared/pipes/contact-name.pipe';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { sortBy } from '@app/utils/sort/sort-by';
import { MeetingViewModel } from '@business/meeting/models/meeting-view-model';
import { MeetingsViewModel } from '@business/meeting/models/meetings-view-model';
import { Observable } from 'rxjs';
import { delayWhen, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MeetingService {
  _meetings: Cached<MeetingsViewModel> | null = null;

  get meetings(): Cached<MeetingsViewModel> {
    this._meetings ??= cachedGroup(
      [
        this.enterpriseContextService.enterprise,
        this.enterpriseContextService.sites,
        this.enterpriseContextService.endUsers,
      ],
      ([{ defaultSite }, { sites, siteMap }, endUsers]) =>
        this.meetingRepository.list().pipe(
          map((meetings) => {
            return {
              defaultSiteId: defaultSite.id,
              endUsers,
              meetings: meetings
                .map((meeting) => this.mapMeeting(meeting, siteMap))
                .sort(sortBy(['owner', 'site', 'name'], false, undefined, true)),
              sites: sites,
            };
          })
        ),
      {
        id: 'enterprise-context:meetings',
      }
    );

    return this._meetings;
  }

  constructor(
    private contactNamePipe: ContactNamePipe,
    private enterpriseContextService: EnterpriseContextService,
    private meetingRepository: MeetingRepository
  ) {}

  create(meeting: MeetingUpdate): Observable<Meeting> {
    return this.meetingRepository.create(meeting).pipe(delayWhen(() => this._meetings.reload(false)));
  }

  delete(id: number): Observable<void> {
    return this.meetingRepository.delete(id).pipe(delayWhen(() => this._meetings.reload(false)));
  }

  resetPinOrToken(id: number, type: ResetMeetingPinOrTokenType): Observable<void> {
    return this.meetingRepository
      .resetPinOrToken(id, type)
      .pipe(delayWhen(() => this.meetings.reload(false)));
  }

  update(id: number, update: MeetingUpdate): Observable<Meeting> {
    return this.meetingRepository.update(id, update).pipe(delayWhen(() => this._meetings.reload(false)));
  }

  private mapMeeting(meeting: Meeting, siteMap: Record<number, Site>): MeetingViewModel {
    const { data, members } = meeting;
    let permanentUrl: string | null = null;

    if (data.uniqueName != null && data.userUrl != null) {
      permanentUrl = data.userUrl.replace(/\?token.*/, `/${data.uniqueName}`);
    }

    return {
      _original: meeting,
      adminPin: data.adminPin,
      dialIn: data.dialIn,
      end: data.planType === PlanType.SCHEDULED ? data.end : null,
      id: data.id,
      members: members.sort(
        sortBy((member) => {
          return `${member.memberRole}:${member.domainName}:${this.contactNamePipe.transform(member)}`;
        })
      ),
      name: data.name,
      owner: members.find((members) => members.userId === data.userId) ?? null,
      permanentUrl,
      phoneNumber: data.phoneNumberList?.find((phoneNumber) => phoneNumber.type !== PhoneNumberType.INTERNAL),
      site: siteMap[data.siteId].name,
      start: data.planType === PlanType.SCHEDULED ? data.start : null,
      userPin: data.userPin,
      userPassphrase: data.userPassphrase,
      userUrl: permanentUrl ?? data.userUrl,
      videoMode: data.videoMode,
    };
  }
}
